import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import FeatureRequestButton from "@/components/FeatureRequestButton";
import {
  LogOut, LayoutGrid, Users, Webhook, Radio, KanbanSquare, Sparkles, Send,
  Wand2, Hash, User, Package, Target, Sun, Moon, Swords, Waves, Menu, X,
  BookOpen, Zap, Flame, Megaphone, UsersRound, RotateCcw,
} from "lucide-react";

const linkBase =
  "flex items-center gap-2 px-3 py-2 text-[13px] font-medium tracking-wide border border-transparent transition-colors whitespace-nowrap";
const active = "bg-[var(--surface-2)] text-[var(--text-primary)] border-[var(--border)]";
const inactive = "text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]";

const ADMIN_LINKS = [
  { to: "/admin", end: true, icon: LayoutGrid, label: "Queue", testId: "nav-admin-queue" },
  { to: "/admin/orchestrate", icon: Wand2, label: "Orchestrate", testId: "nav-admin-orchestrate" },
  { to: "/admin/campaigns", icon: Target, label: "Campaigns", testId: "nav-admin-campaigns" },
  { to: "/admin/drift", icon: Waves, label: "Drift", testId: "nav-admin-drift" },
  { to: "/admin/products", icon: Package, label: "Brands", testId: "nav-admin-products" },
  { to: "/admin/competitors", icon: Swords, label: "Competitors", testId: "nav-admin-competitors" },
  { to: "/admin/training", icon: BookOpen, label: "Training", testId: "nav-admin-training" },
  { to: "/admin/mixes", icon: Flame, label: "Mixes", testId: "nav-admin-mixes" },
  { to: "/admin/ai-settings", icon: Zap, label: "AI", testId: "nav-admin-ai-settings" },
  { to: "/admin/subreddits", icon: Hash, label: "Subreddits", testId: "nav-admin-subs" },
  { to: "/admin/agents", icon: Users, label: "Agents", testId: "nav-admin-agents" },
  { to: "/admin/agent-groups", icon: UsersRound, label: "Groups", testId: "nav-admin-agent-groups" },
  { to: "/admin/broadcast", icon: Megaphone, label: "Broadcast", testId: "nav-admin-broadcast" },
  { to: "/admin/reassignments", icon: RotateCcw, label: "Reassign log", testId: "nav-admin-reassignments" },
  { to: "/admin/webhook", icon: Webhook, label: "Webhook & API", testId: "nav-admin-webhook" },
  { to: "/telegram", icon: Send, label: "Telegram", testId: "nav-telegram" },
];
const AGENT_LINKS = [
  { to: "/agent", end: true, icon: KanbanSquare, label: "My Tasks", testId: "nav-agent-kanban" },
  { to: "/agent/karma", icon: Sparkles, label: "Drift Tasks", testId: "nav-agent-karma" },
  { to: "/agent/profile", icon: User, label: "Profile", testId: "nav-agent-profile" },
  { to: "/telegram", icon: Send, label: "Telegram", testId: "nav-telegram" },
];

function NavItems({ links, onNavigate }) {
  return (
    <>
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onNavigate}
            className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
            data-testid={l.testId}
          >
            <Icon className="w-4 h-4" /> {l.label}
          </NavLink>
        );
      })}
    </>
  );
}

export default function Layout({ children, title, subtitle, right }) {
  const { user, logout } = useAuth();
  const themeCtx = useTheme() || {};
  const theme = themeCtx.theme || "dark";
  const toggle = themeCtx.toggle || (() => {});
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const links = isAdmin ? ADMIN_LINKS : AGENT_LINKS;
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="flex items-center h-14 px-4 sm:px-6 gap-3 sm:gap-6">
          <button
            onClick={() => nav("/")}
            data-testid="brand-home-btn"
            className="flex items-center gap-2 shrink-0"
          >
            <div className="w-2 h-2 bg-[var(--accent)] pulse-dot" />
            <span className="font-display font-black tracking-tighter text-lg">THREADOPS</span>
            <span className="hidden sm:inline text-[10px] font-mono text-zinc-500 uppercase tracking-widest">/crm</span>
          </button>

          {/* Desktop nav — only at xl+ (1280px) so nav stays on one line without wrapping */}
          <nav className="hidden xl:flex items-center gap-1 ml-4 min-w-0 overflow-x-auto">
            <NavItems links={links} />
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <FeatureRequestButton />
            <button
              onClick={toggle}
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "Switch to light" : "Switch to dark"}
              className="p-2 border border-white/10 hover:border-white/25 text-zinc-300 hover:text-white"
            >
              {theme === "dark" ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
            </button>
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-[13px] text-zinc-200">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {user?.role} · {user?.status}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-2 border border-white/10 hover:border-white/25 text-zinc-300 hover:text-white"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              className="xl:hidden p-2 border border-white/10 hover:border-white/25 text-zinc-300"
              aria-label="Menu"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="xl:hidden fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-drawer"
        >
          <div
            className="absolute right-0 top-0 h-full w-72 bg-[var(--bg)] border-l border-white/10 flex flex-col overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-white/10">
              <span className="font-display font-black tracking-tighter">MENU</span>
              <button
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-close"
                className="p-2 border border-white/10 hover:border-white/25"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-3 py-4 border-b border-white/5">
              <div className="text-[13px] text-zinc-200">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {user?.role} · {user?.status}
              </div>
            </div>
            <nav className="p-3 flex flex-col gap-1">
              <NavItems links={links} onNavigate={() => setMobileOpen(false)} />
            </nav>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          {subtitle && (
            <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
              <Radio className="w-3 h-3 text-[var(--accent)]" /> {subtitle}
            </div>
          )}
          {title && (
            <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter">
              {title}
            </h1>
          )}
        </div>
        <div className="shrink-0">{right}</div>
      </div>
      <main className="px-4 sm:px-6 pb-16 flex-1 min-w-0 overflow-x-hidden">{children}</main>
      <footer className="px-4 sm:px-6 py-4 border-t border-white/5 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex flex-col sm:flex-row gap-1 sm:justify-between">
        <span>ThreadOps CRM · Command Center</span>
        <span>v0.1 · {new Date().getUTCFullYear()}</span>
      </footer>
    </div>
  );
}
